import { FC, useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Tabs as UITabs,
  Typography,
  useSnackbar,
  Loader,
} from '@fdha/web-ui-library';
import { SectionType } from '@fdha/common-utils';
import { useChooseMealsData, useMenuOptionFilter } from '@fdha/common-hooks';
import {
  MealOptionType,
  MenuOptionSelectionAvailable as MealItem,
} from '@fdha/graphql-api-patient';
import { useTranslation } from 'react-i18next';

import DishDetails from '../../delivery/DishDetails/DishDetails';

import { ChooseMealsMenuSection } from './ChooseMealsMenuSection';
import { getStyles } from './ChooseMeals.styles';
import ChooseMealsFooter from './ChooseMealsFooter';

interface ChooseMealsProps {
  deliveryId: string;
  itemType: SectionType;
  goToReviewScreen: () => void;
}

interface DishDetailsData {
  open: boolean;
  dishId: string;
  mealOptionType: MealOptionType;
  selectedVariantId?: string;
  selectedCount: number;
  requiredCount: number;
  menuOptionId: string;
  sectionName: SectionType;
}

const ChooseMeals: FC<ChooseMealsProps> = ({ itemType, goToReviewScreen }) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { showSnackbarV2 } = useSnackbar();
  const { t } = useTranslation();

  const {
    loading,
    error,
    entrees,
    snacksAndSides,
    maxEntrees,
    maxSnacksAndSides,
    selectedEntreesCount,
    selectedSnacksAndSidesCount,
    entreeDisplay,
    snacksDisplay,
  } = useChooseMealsData();

  const [dishDetailsData, setDishDetailsData] = useState<DishDetailsData>({
    open: false,
    dishId: '',
    mealOptionType: MealOptionType.Dish,
    selectedCount: 0,
    requiredCount: 1,
    sectionName: 'entreeDishes',
    menuOptionId: '',
  });

  // Show error via snackbar
  useEffect(() => {
    if (error) {
      showSnackbarV2({
        message: `Error loading menu options: ${error}`,
        severity: 'error',
      });
    }
  }, [error, showSnackbarV2]);

  // Separate filter hooks for entrees and snacks
  const entreesFilter = useMenuOptionFilter(entrees);
  const snacksFilter = useMenuOptionFilter(snacksAndSides);

  if (loading) {
    return <Loader />;
  }

  const handleItemClick = (item: MealItem, sectionType: SectionType) => {
    // Determine if it's an entree or snack to get the right counts
    const isEntree = sectionType === 'entreeDishes';
    const currentDisplay = isEntree ? entreeDisplay : snacksDisplay;

    setDishDetailsData({
      open: true,
      dishId: item.dishId,
      mealOptionType: item.mealOptionType as MealOptionType,
      selectedVariantId: '',
      selectedCount: currentDisplay.selected,
      requiredCount: currentDisplay.total,
      menuOptionId: item.menuOptionId,
      sectionName: sectionType,
    });
  };

  const handleCloseDishDetails = () => {
    setDishDetailsData((prev) => ({ ...prev, open: false }));
  };

  const tabs = [
    {
      key: 'entreeDishes',
      title: t('meals:chooseMeals.tabs.entrees'),
    },
    {
      key: 'snacks',
      title: t('meals:chooseMeals.tabs.snacks'),
    },
  ];

  const entreesContent = (
    <Box>
      <ChooseMealsMenuSection
        key="entrees-section"
        title={t('meals:chooseMeals.title.entreeDishes')}
        sectionType={'entreeDishes'}
        items={entrees}
        selectedCount={selectedEntreesCount}
        requiredCount={maxEntrees}
        filterState={entreesFilter}
        onItemClick={handleItemClick}
      />
    </Box>
  );

  const snacksContent = (
    <Box>
      {maxSnacksAndSides > 0 ? (
        <ChooseMealsMenuSection
          key="snacks-section"
          title={t('meals:chooseMeals.title.snacks')}
          sectionType={'snacks'}
          items={snacksAndSides}
          selectedCount={selectedSnacksAndSidesCount}
          requiredCount={maxSnacksAndSides}
          filterState={snacksFilter}
          onItemClick={handleItemClick}
        />
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            variant="body1"
            color="text.secondary"
            i18nKey="meals:chooseMeals.noSnacksRequired"
          >
            {t('meals:chooseMeals.noSnacksRequired')}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const tabContent = {
    entreeDishes: entreesContent,
    snacks: snacksContent,
  };

  // Determine initial index based on itemType
  const initialIndex = itemType === 'entreeDishes' ? 0 : 1;

  const selectedDishItem = {
    dishId: dishDetailsData.dishId,
    mealOptionType: dishDetailsData.mealOptionType,
    menuOptionId: dishDetailsData.menuOptionId,
    selectedVariantId: dishDetailsData.selectedVariantId,
    sectionName: dishDetailsData.sectionName,
  };

  return (
    <Box style={styles.mainContainer}>
      <UITabs
        tabs={tabs}
        tabContent={tabContent}
        initialIndex={initialIndex}
        indicatorPosition="top"
        variant="fullWidth"
      />

      {/* Footer with summary and actions */}
      <ChooseMealsFooter handleReviewSelection={goToReviewScreen} />

      {/* Dish Details Modal */}
      {dishDetailsData.open && (
        <DishDetails
          dishItem={selectedDishItem}
          selectedCount={dishDetailsData.selectedCount}
          requiredCount={dishDetailsData.requiredCount}
          onClose={handleCloseDishDetails}
          isPreviewMode={false}
          open={dishDetailsData.open}
        />
      )}
    </Box>
  );
};

export default ChooseMeals;
